// Fleets in flight (spec 15c §3.5, GDD 08): own bright, alliance dimmed, detected hostile pulsing red.
// Dashes drift towards the target and only cover the way still ahead; own/alliance fleets get a
// chevron at their current position.
// Sizes are in screen pixels (divided by the camera scale).
import { Container, Graphics } from 'pixi.js';
import { MAP_COLORS } from '../../theme';
import { dashSegments } from '../dashes';
import {
  type FleetRelation,
  fleetMarker,
  type MapFleet,
  markerPoint,
  pathLength,
  remainingPath,
} from '../fleets';
import { FLEET_MARKER, placeGlyph } from '../glyphs';

const DASH_PX = 6;
const GAP_PX = 5;
const DRIFT_PX_PER_S = 12;
const MARKER_PX = 8;
const STYLE: Record<FleetRelation, { color: number; alpha: number }> = {
  own: { color: MAP_COLORS.own, alpha: 0.95 },
  alliance: { color: MAP_COLORS.ally, alpha: 0.5 },
  detected: { color: MAP_COLORS.hostile, alpha: 0.9 },
};

export interface FleetsLayer {
  container: Container;
  update(fleets: readonly MapFleet[], selectedId: string | null, now: number, scale: number): void;
}

export function fleetsLayer(): FleetsLayer {
  const lines = new Graphics();
  const markers = new Graphics();
  const container = new Container();
  container.addChild(lines, markers);
  return {
    container,
    update(fleets, selectedId, now, scale) {
      lines.clear();
      markers.clear();
      const pulse = 0.55 + 0.45 * Math.sin(now / 250);
      const drift = ((now / 1000) * DRIFT_PX_PER_S) / scale;
      for (const fleet of fleets) {
        const style = STYLE[fleet.relation];
        const alpha = fleet.relation === 'detected' ? style.alpha * pulse : style.alpha;
        const selected = fleet.id === selectedId;
        const ahead = remainingPath(fleet, now);
        if (ahead && fleet.path) {
          // Shift by the distance flown, so the dashes stay put on the route as the start moves.
          const flown = pathLength(fleet.path) - pathLength(ahead);
          const dashes = dashSegments(ahead, DASH_PX / scale, GAP_PX / scale, drift - flown);
          for (const [a, b] of dashes) lines.moveTo(a.x, a.y).lineTo(b.x, b.y);
          if (dashes.length > 0) {
            lines.stroke({ width: (selected ? 3 : 1.5) / scale, color: style.color, alpha });
          }
        }
        const pose = fleetMarker(fleet, now);
        const at = markerPoint(fleet, now, scale);
        if (fleet.relation === 'detected') {
          markers.circle(at.x, at.y, (MARKER_PX * 0.8) / scale).fill({ color: style.color, alpha });
        } else {
          markers
            .poly(
              placeGlyph(
                FLEET_MARKER,
                pose.point.x,
                pose.point.y,
                MARKER_PX / scale,
                pose.angle + Math.PI / 2
              )
            )
            .fill({ color: style.color, alpha: fleet.relation === 'own' ? 1 : 0.7 });
        }
        if (selected) {
          markers
            .circle(at.x, at.y, (MARKER_PX * 2) / scale)
            .stroke({ width: 1, color: MAP_COLORS.selected, pixelLine: true });
        }
      }
    },
  };
}
